import { useCallback, useMemo } from 'react';
import { triggerPrismSync } from '../../api/assets';
import SyncBar, { SyncBarState } from '../../components/SyncBar';
import { loadPrismState, usePrismSyncState } from '../../utils/prismReadLocal';
import { useSyncPolling } from '../../utils/useSyncPolling';

// 材料识别 / 材料加工 / 矩阵识别三页共用的 prism 同步状态条 (2026-10-20).
// prism 每小时 :30 增量同步七个数据集; 这里把它们合成一行显示: 任一在跑 = 同步中, 任一失败 = 失败,
// 最近成功时刻取最大. 归档回填没追平时提示「回填中」(页面在读路径切换前仍直连 prism, 不受影响).

function parseStats(raw?: string): Record<string, unknown> {
    try {
        return JSON.parse(raw || '{}') as Record<string, unknown>;
    } catch {
        return {};
    }
}

/** 每小时同步一轮的步骤 (与后端 doSyncRound 的顺序一致). */
const ROUND_STEPS = ['recognition', 'feedback', 'archive', 'label', 'rating', 'matrix', 'rollup'] as const;

export default function PrismSyncBar({ onSynced }: { onSynced: () => void }) {
    const state = usePrismSyncState();

    const bar: SyncBarState | null = useMemo(() => {
        if (!state) return null;
        // 只看每小时一轮的 7 个步骤: history (一次性历史并表) / rollup_pending (待办日期) 不是同步轮的一部分,
        // 算进来会把耗时、触发方式带偏 (dev 实测历史迁移的 957s 被加进了「耗时」).
        const ds = ROUND_STEPS.map((k) => state.datasets[k]).filter((d) => !!d);
        const failed = ds.filter((d) => d!.status === 'failed');
        const latest = ds.reduce((a, d) => (d!.lastRunAt > (a?.lastRunAt ?? 0) ? d : a), ds[0]);
        return {
            status: state.running ? 'running' : failed.length > 0 ? 'failed' : 'idle',
            lastOkAt: state.lastOkAt,
            durationMs: ds.reduce((sum, d) => sum + (d!.durationMs || 0), 0),
            failStreak: Math.max(0, ...failed.map((d) => d!.failStreak)),
            lastError: failed.map((d) => `${d!.dataset}: ${d!.lastError}`).join('\n'),
            triggerBy: latest?.triggerBy || 'timer',
        };
    }, [state]);

    const backfilling = useMemo(() => {
        const st = parseStats(state?.datasets.archive?.stats);
        return st.caughtUp === false;
    }, [state]);

    const poll = useCallback(async () => {
        const s = await loadPrismState(true);
        const failed = s ? Object.values(s.datasets).filter((d) => d && d.status === 'failed') : [];
        return {
            running: !!s?.running,
            error: failed.map((d) => `${d!.dataset}: ${d!.lastError}`).join('; '),
        };
    }, []);
    const trigger = useCallback(() => triggerPrismSync(false), []);
    const { syncing, start } = useSyncPolling({ trigger, poll, onDone: onSynced });

    const minute = String(state?.minute ?? 30).padStart(2, '0');
    return (
        <SyncBar
            state={bar}
            syncing={syncing}
            onSync={start}
            sourceText={`来源 prism · 每小时 :${minute} 自动`}
            note={backfilling ? <span className="text-amber-600">回填中</span> : undefined}
        />
    );
}
